import { ReactElement, useEffect } from 'react'
import { QuantitySelector } from '../../../../../../src/ui-components/products/QuantitySelector'
import { observer } from 'mobx-react-lite'
import { useUserStore } from '../../../../../../src/store/user/UserStore'
import {
  NonCachedSearchType,
  ProductLocationModel,
  ProductModel,
} from 'interfaces'
import { ICartProductData } from 'cart'
import { Vehicle } from '../../../../../../src/store/models/Vehicles'
import { LinearProgress } from '@mui/material'
import { ISeller } from '../../../../../../src/services/MultiSellerServicesProvider'
import { useSearchStore } from '../../../../../../src/store/search/SearchStore'
import { translateApiScheduleText } from '../../../../../../src/helpers/utils'

interface Props {
  seller: ISeller
  product: ProductModel
  location: ProductLocationModel
  vehicle: Vehicle
  cartProduct: ICartProductData
  qty: number
  setQty: (qty: number) => void
}

const QuantitySelectorWrapper = observer<Props>(
  (props: Props): ReactElement => {
    const { product, location, cartProduct, qty, setQty } = props
    const userStore = useUserStore()
    const searchStore = useSearchStore()
    const { lastNonCachedSearch } = searchStore

    const orderIfNotAvailable =
      userStore?.preferences?.findit_orderIfNotAvail === 'true'
    const setInitialValueToOne =
      userStore?.preferences?.display_perCarQtyOne === 'true'

    const {
      getInCartQtyByLocation,
      updateQuantity,
      priceSpinner,
      setPriceSpinner,
    } = cartProduct

    const inCartQty = getInCartQtyByLocation(location)

    const initialPerCarQty = setInitialValueToOne ? 1 : product.perCarQty

    useEffect(() => {
      setQty(inCartQty ?? initialPerCarQty)
    }, [inCartQty, initialPerCarQty, setQty])

    const displayMinQty =
      (location?.displayMinQty > location?.qtyAvailable
        ? location?.qtyAvailable
        : location?.displayMinQty) ?? 1

    const initialValueIfNotInCart =
      lastNonCachedSearch === NonCachedSearchType.searchByMultiPartsInquiry ||
      lastNonCachedSearch === NonCachedSearchType.searchBySpecificParts
        ? location?.buyQty
        : Math.max(
            1,
            displayMinQty,
            location?.buyIncrement || 0,
            initialPerCarQty
          )

    useEffect(() => {
      const initialSelectorQty: number =
        inCartQty > 0
          ? inCartQty
          : searchStore.getUpdatedPartInfoDetails(product)?.updatedQty > 0
            ? searchStore.getUpdatedPartInfoDetails(product)?.updatedQty
            : initialValueIfNotInCart

      setQty(initialSelectorQty)
    }, [inCartQty, initialValueIfNotInCart, product, searchStore, setQty])

    const handleOnQtyChange = async (newQty: number): Promise<void> => {
      setPriceSpinner(true)
      await updateQuantity(newQty, location)
    }

    const isPrimary = location?.locType === 'primary'

    return (
      <>
        <QuantitySelector
          orderIfNotAvailable={orderIfNotAvailable && isPrimary}
          onChange={handleOnQtyChange}
          onRemove={() => undefined}
          minQty={Math.max(location?.minQty, location?.buyIncrement || 0)}
          maxQty={location?.qtyAvailable}
          stepSize={location?.buyIncrement}
          onInputHoverTooltipMsg={translateApiScheduleText(location?.schedule)}
          orderNumber={product.orderNumber}
          isStoked={product.isStocked}
          value={qty?.toString()}
          setValue={(val) => setQty(Number(val))}
          isCondensedView={false}
          isLoading={priceSpinner}
        />
        {priceSpinner && <LinearProgress />}
      </>
    )
  }
)

export default QuantitySelectorWrapper
